import {PolymerElement} from '@polymer/polymer';
import {ScopeBinding, ScopeRoot} from '../behaviors/keli-binding-behavior';
import {Debouncer} from "@polymer/polymer/lib/utils/debounce";
import {timeOut} from "@polymer/polymer/lib/utils/async";

class KeliGridController extends ScopeRoot(ScopeBinding(PolymerElement)) {
  static get is() {
    return 'keli-grid-controller'
  }

  static get properties() {
    return {
      columns: {type: Array},
      element: {type: Object},
      manualKeliReady: {type: Boolean, value: true},
      dataLoaded: {type: Boolean},
      isDatabaseTemplate: {type: Boolean},
      renderSequenceNumber: {type: Number, value: 0}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli(['keliAccepted', 'deleteGridRow']);
    this.addOnKeli({
      'uiFocus': 'handleFieldFocus',
      'uiGridRowFocus': 'handleRowFocus',
      'uiUserChangedRow': 'handleUserChangedRow',
      'uiObjectVisChanged': 'handleObjectVisChanged',
      'uiReloadData': 'handleReloadData',
      'uiResetGridInsertRow': 'handleResetGridInsertRow',
      'uiSaveAll': 'handleSaveAll'
    });
  }

  connectedCallback() {
    super.connectedCallback();
  }

  async elementReady(element) {
    this.element = element;

    await this.changeKeliScopeRootName(this.element.keliname);
    await this.keliReady(this.element.getKeliScope());

    this._boundHandlePanelAllDataLoaded = this.handlePanelAllDataLoaded.bind(this);
    this.element.addEventListener('panelAllDataLoaded', this._boundHandlePanelAllDataLoaded);

    this.executeOnKeliReadyComplete(async () => {
      await this.initialGridSetup();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.element.removeEventListener('panelAllDataLoaded', this._boundHandlePanelAllDataLoaded);
    this.element = null;
  }

  async handlePanelAllDataLoaded() {
    await this.setupMenu();
  }

  async setupMenu() {
    keliUITimingEvents && keliUITimer('UI_Grid_setupMenu');
    const gridMenuItems = await this.runKeliFunction('getControlMenuDefinitions');
    const menuItems = [];
    for (const item of gridMenuItems.list) {
      menuItems.push({
        id: item.id,
        name: item.name,
        parentCaption: item.parentcaption,
        region: item.region,
        hotkey: item.hotkey,
        keliTargetScopeRoot: this,
        registerToElement: this.element
      });
    }
    this.dispatchEvent(new CustomEvent('keliControlMenuReady', {
      bubbles: true, composed: true,
      detail: {list: menuItems}
    }));
    keliUITimingEvents && keliUITimer('UI_Grid_setupMenu', true);
  }

  updateLastSelectedRow(rowId) {
    if (!this.isConnected) return;
    this.setKeliRowId(rowId);
  }

  async handleFieldFocus(event) {
    this.element?.focusOn(null, event.field);
  }

  async handleRowFocus(event) {
    this.element?.focusOn(event.rowid, null, !this.element.focused);
  }

  async handleUserChangedRow(event) {
    const insertRowId = this.element?._insert()?.getAttribute('row-id');
    if (insertRowId && event.rowid === insertRowId) {
      this.element.insertRowHasChanges = true;
    }
  }

  async handleObjectVisChanged(event, templateInstance) {
    let objectName = event.objectname;
    if (Array.isArray(objectName)) {
      if (objectName.length > 1) {
        objectName = objectName.slice(0, -1).join('.');
      } else {
        objectName = objectName.join('');
      }
    }
    if (await this.runKeliFunctionIn(templateInstance, 'get', `${objectName}.controltype`, {p: true, peek: true}) === 'menuitem') {
      this.dispatchEvent(new CustomEvent('keliControlObjectVisChanged', {
        bubbles: true, composed: true,
        detail: {
          id: `${this.element.keliname}_${objectName}`,
          keliname: objectName,
          event,
          controller: this
        }
      }));
      return;
    }

    // Ignore vis changes that will not modify the displayed columns
    if (event.value === false && !this.columns?.map(c => c.name).includes(objectName)) {
      return;
    }

    this.clearAllData();
    await this.resetColumns(templateInstance);
  }

  async handleReloadData(event, templateInstance) {
    if (!this.dataLoaded) {
      return;
    }
    await this.loadAllData(templateInstance, true, event?.rowtoselect);
  }

  async handleResetGridInsertRow(event, templateInstance) {
    if (!this.dataLoaded) {
      return;
    }

    const insertRowWasFocused = this.element._insert()?.contains(this.element.AdjacentElement.currentElement);
    this.element._insert()?.remove();

    await this.createInsertRow(templateInstance);

    if (insertRowWasFocused) {
      this.element.focusInsertRow(null, true);
    }
  }

  async handleSaveAll(event) {
    await this.element.saveInsertRow(this.element._insert(), !!event.createnewrow);
  }

  async initialGridSetup(templateInstance) {
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    this.isDatabaseTemplate = !!(await this.runKeliFunctionIn(templateInstance, 'isDB'));
    if (!this.isConnected) return;

    // Build the column information
    this.columns = await this.runKeliFunctionIn(templateInstance, 'getGridColumns');
    if (!this.isConnected) return;
    if (!this.columns || !this.columns.length) return;

    // Generate columns markup
    const headerRow = this.createRow(null, true);
    if (headerRow) {
      this.element.$.header.append(headerRow);
    }

    await this.loadAllData(templateInstance, false);
    if (!this.isConnected) return;

    if (this.element.insertable !== false) {
      await this.createInsertRow(templateInstance);
      if (!this.isConnected) return;
    }
    this.element.dispatchEvent(new CustomEvent('dataUpdated', {detail: {initial: true}}));
  }

  async resetColumns(templateInstance) {
    return new Promise(resolve => {
      this._handleResetColumnsJob = Debouncer.debounce(this._handleResetColumnsJob, timeOut.after(25), async () => {
        this.clearAllData({header: true, insert: true});
        await this.initialGridSetup(templateInstance);
        resolve();
      });
    });
  }

  clearAllData({header, insert} = {header: false, insert: false}) {
    if (header) {
      while (this.element.$.header.firstElementChild) {
        this.element.$.header.firstElementChild.remove();
      }
    }
    if (insert) {
      this.element._insert()?.remove();
    }
    while (this.element.$.body.firstElementChild) {
      this.element.$.body.firstElementChild.remove();
    }
  }

  async loadAllData(templateInstance, reload = true, rowToSelect = null) {
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    const cancelLoading = (renderSequence) => {
      if (!this.isConnected) return true;
      if (renderSequence && this.renderSequenceNumber !== renderSequence) return true;
    };

    try {
      keliUITimingEvents && keliUITimer('UI_Grid_loadAllData');
      const renderSequence = ++this.renderSequenceNumber;

      const startingRowId = this.element.lastFocusedInfo?.rowId;
      const controller = this;
      return await this.runKeliFunctionIn(templateInstance, 'supercede', {
        domain: controller.element.keliname,
        func: async function () {

          const rowIds = await this.getGridRowIds();
          await this.checkSuperceded();
          if (cancelLoading(renderSequence)) return;

          const existingRows = controller.element.$.body.querySelectorAll('.row');
          const existingMap = {};
          for (const element of existingRows) {
            existingMap[element.getAttribute('row-id')] = element;
          }

          const finalBodyElements = [];
          for (const rowId of rowIds) {
            let element = existingMap[rowId] || controller.createRow(rowId);

            // Re-pull the displayValue if we're reloading.
            if (reload && existingMap[rowId]) {
              const cells = element.querySelectorAll('.cell');
              for (const cell of cells) {
                const cellField = cell.firstElementChild;
                if (!cellField?.keliname) {
                  continue;
                }
                const scopeVarInfo = cellField.getKeliScopevarInfo?.().byScopevar.displayvalue;
                if (!scopeVarInfo) {
                  continue;
                }
                cellField.getSingleScopeVar(templateInstance, scopeVarInfo, false);
              }
            }

            finalBodyElements.push(element);

            await this.checkSuperceded();
            if (cancelLoading(renderSequence)) return;
          }

          // Decide if we should refocus the grid after the mutations are done.
          const refocus = controller.element.focused;

          // Remove any nodes that do not belong in the DOM anymore.
          const removeNodes = Array.from(existingRows).filter(element => {
            return !finalBodyElements.includes(element) && element !== controller.element._insert();
          });
          for (const element of removeNodes) {
            if (refocus && element.contains(controller.element.AdjacentElement.currentElement)) {
              await controller.element.moveFocusedRow(false, true);
            }
            element.remove();
          }

          // Smartly update the DOM with the inserted or updated row positions.
          const parentNode = controller.element.$.body;
          let previousElement;
          for (const element of finalBodyElements) {
            if (element === controller.element._insert()) {
              continue;
            }

            if (!previousElement && parentNode.firstElementChild === element) {
              previousElement = element;
              continue;
            }

            if (previousElement && previousElement === element.previousElementSibling) {
              previousElement = element;
              continue;
            }

            previousElement = element;
            if (element.isConnected) {
              controller.getKeliScopeRoot().moveDOMTreeWithScopeRoot(element, controller, parentNode, controller.element._insert());
            } else {
              parentNode.insertBefore(element, controller.element._insert());
            }
          }

          if (!controller.dataLoaded) {
            controller.dataLoaded = true;
            controller.dispatchEvent(new CustomEvent('dataLoaded', {
              bubbles: true, composed: true,
              detail: {element: controller.element}
            }));
          }

          if (cancelLoading(renderSequence)) return;
          if (controller.element.lastFocusedInfo?.rowId !== startingRowId) {
            rowToSelect = null;
          }
          controller.element.dispatchEvent(new CustomEvent('dataUpdated', {detail: {focus: refocus, rowToSelect}}));
        }
      });
    } catch (error) {
      if (!error.superceded) {
        console.error(error);
      }
    } finally {
      keliUITimingEvents && keliUITimer('UI_Grid_loadAllData', true);
    }
  }

  createRow(rowId, header = false, insert = false) {
    if (!this.columns?.length) {
      return;
    }

    const row = document.createElement('div');
    if (rowId) {
      row.setAttribute('row-id', rowId);
    }
    row.classList.add('row');
    if (insert) {
      row.classList.add('insert');
    }

    for (const column of this.columns) {
      const cell = document.createElement('span');
      cell.classList.add('cell');
      if (column.width) {
        cell.style.maxWidth = `${parseInt(column.width, 10)}px`;
      }

      if (header) {
        cell.textContent = column.caption;
        this.element.addTooltip(cell, null, {
          content: column.caption
        });
      } else {
        const control = this.createControl(column, rowId);
        if (insert) {
          control.preventKeliAcceptOnChangeThenRevert = true;
        }
        cell.append(control);
      }
      row.append(cell);
    }

    // Append the icon row
    if (this.isDatabaseTemplate) {
      const editCell = document.createElement('span');
      editCell.classList.add('cell', 'icon');
      const editIconName = (insert ? this.element.iconInsert : (!header ? this.element.iconEdit : null));
      if (editIconName) {
        const icon = document.createElement('sl-icon');
        icon.name = editIconName;
        editCell.append(icon);
        editCell.addEventListener('click', event => {
          event.stopPropagation();
          if (this.element) {
            this.element.dispatchEvent(new CustomEvent('activateRowAction', {detail: {rowId}}));
          }
        });
      }
      row.append(editCell);

      const deleteCell = document.createElement('span');
      deleteCell.classList.add('cell', 'icon');
      const deleteIconName = (!header ? this.element.iconDelete : null);
      if (deleteIconName) {
        const icon = document.createElement('sl-icon');
        icon.classList.add('delete-icon');
        icon.name = deleteIconName;
        deleteCell.append(icon);
        deleteCell.addEventListener('click', event => {
          event.stopPropagation();
          this.dispatchEvent(new CustomEvent('deleteGridRow', {
            detail: {
              rowId,
              isInsertRow: (this.element._insert()?.getAttribute('row-id') === rowId)
            }
          }));
        });
      }
      row.append(deleteCell);
    }

    return row;
  }

  createControl(column, rowId) {
    const meta = KeliInterface.registeredKeliControlTypes[column.controltype];
    if (!meta) {
      return;
    }

    const control = document.createElement(meta.tagName);
    control.keliname = column.name;
    control.boundKeliRowId = rowId;
    control.keliTargetScopeRoot = this;
    control.forcePropagation = true;
    control.unbindScopevars = ['caption', 'focus'];
    control.chrome = false;
    return control;
  }

  async saveInsert() {
    await this.runKeliFunction('commitGridInsert');
  }

  async createInsertRow(templateInstance) {
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    if (this.element._insert()) {
      return;
    }

    const insertRowId = await this.runKeliFunctionIn(templateInstance, 'getGridInsertRow');
    if (!this.isConnected) return;

    if (this.element._insert()) {
      return;
    }

    const insertRowElement = this.createRow(insertRowId, false, true);
    if (insertRowElement) {
      this.element.$.body.append(insertRowElement);
    }
  }
}

customElements.define(KeliGridController.is, KeliGridController);
export {KeliGridController};
